const en = {
  translation: {
    // ── Brand ─────────────────────────────────────────────────────────────────
    brand: { name: 'OnCall', tagline: 'Alert Notifier' },

    // ── Navigation ────────────────────────────────────────────────────────────
    nav: {
      dashboard: 'Dashboard',
      sessions: 'Sessions',
      notifications: 'Notifications',
      myPage: 'My Page',
      admin: 'Admin',
      expandSidebar: 'Expand sidebar',
      collapseSidebar: 'Collapse sidebar',
      openMenu: 'Open menu',
      viewAccount: 'View account',
      logout: 'Logout',
      installApp: 'Install app',
    },

    // ── Settings menu ─────────────────────────────────────────────────────────
    settings: {
      language: 'Language',
      theme: 'Theme',
      themeDark: 'Dark',
      themeLight: 'Light',
      themeSystem: 'System',
    },

    // ── Login ─────────────────────────────────────────────────────────────────
    login: {
      title: 'OnCall Alert Notifier',
      subtitle: 'Internal tool for on-call incident management',
      features: {
        gmail: 'Gmail alert monitoring',
        phone: 'Phone call notifications',
        calendar: 'Calendar on-call tracking',
        reports: 'Monthly reports',
      },
      signIn: 'Sign in with Google',
      hint: 'Sign in with your organizational Google Workspace account',
      errors: {
        access_denied: 'You denied access to your Google account. Please try again.',
        email_not_allowed: 'Your email is not on the allowlist. Contact your administrator.',
        state_mismatch: 'Login session expired or invalid. Please try again.',
        unknown: 'An unexpected error occurred. Please try again.',
      },
    },

    // ── Dashboard ─────────────────────────────────────────────────────────────
    dashboard: {
      onCallStatus: 'On-Call Status',
      onDuty: 'On Duty',
      offDuty: 'Off Duty',
      ends: 'Ends {{time}}',
      nextShift: 'Next Shift',
      noneScheduled: 'None scheduled',
      noUpcomingShifts: 'No upcoming shifts in 14 days',
      recentIncidents: 'Recent Incidents',
      noIncidents: 'No incidents yet. Enjoy the quiet!',
      activeIncidentTitle: 'Active Incident',
      activeIncidentStarted: 'Started {{time}}',
      confirmIncident: 'Confirm',
      loadError: 'Failed to load dashboard. The backend may be unavailable.',
      table: {
        started: 'Started',
        status: 'Status',
        confirmed: 'Confirmed',
        notes: 'Notes',
        investigation: 'Investigation',
      },
    },

    // ── Incident statuses ─────────────────────────────────────────────────────
    incidentStatus: {
      ACTIVE: 'Active',
      CONFIRMED: 'Confirmed',
      RESOLVED: 'Resolved',
      TIMEOUT: 'Timeout',
      SILENT: 'Silent',
      STALLED: 'Stalled',
      FAILED: 'Failed',
    },

    // ── Sessions / Report page ────────────────────────────────────────────────
    sessions: {
      title: 'On-Call Summary',
      me: 'Me',
      manualIncident: 'Manual incident',
      manual: 'Manual',
      investigated: 'investigated',
      month: 'Month',
      exportExcel: 'Export Excel',
      loadError: 'Failed to load sessions for {{month}}.',
      stats: {
        totalOnCall: 'Total on-call time',
        paidHours: 'Paid hours (outside {{start}}:00\u2013{{end}}:00 + lunch)',
        weekendHours: 'Weekend hours',
        investigationTime: 'Investigation time',
        sessionCount_one: '{{count}} session',
        sessionCount_other: '{{count}} sessions',
      },
      calendar: {
        today: 'Today',
        prevMonth: 'Previous month',
        nextMonth: 'Next month',
        more_one: '+{{count}} more',
        more_other: '+{{count}} more',
        nothingOnDay: 'Nothing scheduled on this day.',
        incidentCount_one: '{{count}} incident',
        incidentCount_other: '{{count}} incidents',
        legend: {
          session: 'Session',
          incident: 'Unmatched incident',
        },
      },
      table: {
        session: 'Session',
        start: 'Start',
        end: 'End',
        duration: 'Duration',
        incidents: 'Incidents',
        actions: 'Edit',
        viewIncidents: 'View incidents',
        unmatchedIncident: 'Unmatched incident',
      },
      dialog: {
        incidentNotes: 'Incident Notes',
        notes: 'Notes',
        notesHelper: '{{count}}/300 \u2014 what happened, what was investigated?',
        notesCount: '{{count}}/300',
        investigationTime: 'Investigation time (minutes)',
        investigationHelper: 'Time spent actively investigating this incident',
        cancel: 'Cancel',
        save: 'Save',
        saving: 'Saving\u2026',
        addIncident: 'Add Incident',
        date: 'Date',
        time: 'Time',
        creating: 'Creating\u2026',
        create: 'Create',
        incidentsTitle: 'Incidents',
        onCallSession: 'On-Call Session',
        noIncidents: 'No incidents recorded for this session.',
        deleteConfirm: 'Confirm?',
        deleteIncidentTooltip: 'Delete incident',
        confirmDeletion: 'Click again to confirm deletion',
        editNotesTooltip: 'Edit notes / investigation time',
        addIncidentBtn: 'Add incident',
        close: 'Close',
      },
    },

    // ── Preferences / Notifications ───────────────────────────────────────────
    preferences: {
      loadError: 'Failed to load notification channels. Please refresh the page.',
      phoneNumbers: 'Phone Numbers',
      countOf: '{{count}} / {{max}}',
      removePhone: 'Remove phone number',
      noPhones: 'No phone numbers registered yet',
      phoneLabel: 'Phone number',
      phonePlaceholder: '+375291234567',
      label: 'Label',
      labelPlaceholder: 'main',
      sendCode: 'Send code',
      alreadyRegistered: 'Already registered',
      invalidFormat: 'Invalid phone number',
      labelTaken: 'Label already used',
      charCount: '{{count}}/20',
      codeSent: 'Code sent to <strong>{{number}}</strong>. Enter it below:',
      verificationCode: 'Verification code',
      codePlaceholder: '123456',
      verify: 'Verify',
      verifying: 'Verifying\u2026',
      back: 'Back',
      invalidCode: 'Wrong code',
      resend: 'Resend',
      resendIn: 'Resend ({{seconds}}s)',
      sendAgain: 'Send again',
      addPhone: 'Add phone number',
      verifyFirst: 'Verify your first number above before adding another',
      verified: 'Verified',
      pending: 'Pending',
      removeTooltip: 'Remove number',
      channelConflict: 'Cannot remove this number \u2014 it is still assigned to a notification channel. Remove it from the channel first.',
      channels: 'Notification Channels',
      notifEnabled: 'Notifications enabled',
      notifEnabledDesc: 'Calls will be placed when an incident is detected.',
      notifDisabled: 'Notifications disabled',
      notifDisabledDesc: 'No calls will be placed even if an incident is detected.',
      noChannels: 'No channels configured yet',
      addChannel: 'Add channel',
      verifyPhoneFirst: 'Verify a phone number above to add channels',
      maxChannels: 'Maximum {{count}} channels allowed. Contact an admin to increase the limit.',
      saveChanges: 'Save changes',
      savedOk: 'Saved successfully',
      saveFailed: 'Failed to save \u2014 please try again.',
      allMustHavePhone: 'All channels must have a phone number selected.',
      duplicatePriority: 'A channel with priority {{priority}} and this number already exists.',
      deleteChannelTitle: 'Remove channel',
      deleteChannelMessage: 'Remove channel <strong>{{address}}</strong>? This cannot be undone.',
      channel: {
        phone: 'Channel address',
        selectNumber: 'Select a verified number\u2026',
        required: 'Required \u2014 select a verified number',
        priority: 'Priority (same \u2192 fires in parallel)',
        ringDuration: 'Ring duration: {{seconds}}s',
        ringMin: '15s',
        ringMax: '60s',
        maxRetries: 'Attempts (1\u20135)',
        retryInterval: 'Interval, min (1\u201310)',
        notConfigured: 'Not configured',
        details: 'Priority {{priority}} \u00b7 {{ring}}s \u00b7 {{retries}}\u00d7{{interval}}min',
        editTooltip: 'Edit channel',
        removeTooltip: 'Remove channel',
        save: 'Save',
        saving: 'Saving\u2026',
      },
      deletePhoneTitle: 'Remove phone number',
      deletePhoneMessage: 'Remove <strong>{{number}}</strong> ({{label}})? This cannot be undone.',
      delete: 'Remove',
    },

    // ── Account page ──────────────────────────────────────────────────────────
    account: {
      accountDetails: 'Account Details',
      fullName: 'Full name',
      email: 'Email address',
      loginSecurity: 'Login & Security',
      loginProvider: 'Login provider',
      google: 'Google Workspace',
      microsoft: 'Microsoft 365',
      role: 'Role',
      administrator: 'Administrator',
      user: 'User',
      calendar: 'Calendar',
      edit: 'Edit',
      onCallCalendar: 'On-call calendar',
      selectCalendars: 'Select from your Google calendars',
      icsUrl: 'VictorOps ICS URL',
      icsUrlHelp: 'Synced into your primary Google Calendar every 15 minutes',
      eventTitle: 'Event title',
      eventTitlePlaceholder: 'e.g. On Call',
      eventTitleHelp: 'Custom title for synced events',
      eventTitleDefault: 'VictorOps summary',
      icsNotConfigured: 'Not configured',
      icsUrlInvalid: 'Enter a valid URL (https://…)',
      icsUrlHost: 'Must be a VictorOps URL (portal.victorops.com)',
      icsNeedsAttention: 'ICS calendar sync is paused — the link looks invalid or expired. Update the VictorOps ICS URL and save to resume.',
      eventColorLabel: 'On-call event color',
      colorFilter: 'Event color filter',
      noFilter: 'Default color',
      colorHelp: 'Color applied to synced on-call events',
      timezoneSchedule: 'Timezone & Schedule',
      timezone: 'Timezone',
      timezonePlaceholder: 'Europe/Warsaw',
      timezoneHelp: 'IANA timezone used for calendar display and report timestamps',
      officeHours: 'Office hours',
      starts: 'Starts',
      ends: 'Ends',
      saveFailed: 'Failed to save. Please try again.',
      logOut: 'Log out',
      loggingOut: 'Logging out\u2026',
      save: 'Save',
      saving: 'Saving\u2026',
      cancel: 'Cancel',
      copied: 'Copied!',
      copy: 'Copy',
      primaryCalendar: 'Primary calendar',
      colors: {
        Lavender: 'Lavender',
        Sage: 'Sage',
        Grape: 'Grape',
        Flamingo: 'Flamingo',
        Banana: 'Banana',
        Tangerine: 'Tangerine',
        Peacock: 'Peacock',
        Graphite: 'Graphite',
        Blueberry: 'Blueberry',
        Basil: 'Basil',
        Tomato: 'Tomato',
      },
    },

    // ── Admin page ────────────────────────────────────────────────────────────
    admin: {
      tabs: { users: 'Users', events: 'System Events', allowlist: 'Email Allowlist' },
      users: {
        nameEmail: 'Name / Email',
        lastSeen: 'Last seen',
        never: 'Never',
        provider: 'Provider',
        role: 'Role',
        notifs: 'Notifs',
        accountCol: 'Account',
        testAlerts: 'Test Alerts',
        limits: 'Limits',
        actions: 'Actions',
        user: 'User',
        admin: 'Admin',
        deleteUser: 'Delete user',
        deleteConfirm: 'Are you sure you want to delete {{name}} ({{email}})? This action cannot be undone.',
        deleteBtn: 'Delete',
        forceLogout: 'Force logout',
        forceLogoutConfirm: 'Log {{name}} out of all sessions?',
        forceLogoutBtn: 'Log out',
        limitsTitle: 'User limits \u2014 {{name}}',
        maxPhones: 'Max phone numbers',
        maxChannels: 'Max notification channels',
        testAlertsTooltip: 'When enabled, test alert emails trigger calls like real incidents',
        limitsTooltip: 'Phones: {{phones}} \u00b7 Channels: {{channels}}',
      },
      events: {
        type: 'Event type',
        allTypes: 'All types',
        from: 'From',
        to: 'To',
        clearDates: 'Clear dates',
        noEvents: 'No events found',
        forType: 'for type {{type}}',
        selectAll: 'Select all on this page',
        selected: '{{count}} selected',
        deleteSelected: 'Delete selected',
        deleting: 'Deleting\u2026',
        page: 'Page {{n}}',
        pageLast: 'Page {{n}} (last)',
        previous: 'Previous',
        next: 'Next',
        deleteTitle: 'Delete events',
        deleteConfirm_one: 'Are you sure you want to delete {{count}} event? This action cannot be undone.',
        deleteConfirm_other: 'Are you sure you want to delete {{count}} events? This action cannot be undone.',
        types: {
          RUNTIME_EXCEPTION: 'Runtime Exceptions',
          INCIDENT: 'Incidents',
          TEST_ALERT: 'Test Alerts',
          SYSTEM: 'System',
          AUTH: 'Auth Violations',
          ACCESS_DENIED: 'Access Denied',
        },
      },
      allowlist: {
        addTitle: 'Add allowed email',
        hint: '',
        emailLabel: 'Email',
        emailPlaceholder: 'user@company.com',
        noteLabel: 'Note (optional)',
        add: 'Add',
        tableEmail: 'Email',
        tableType: 'Type',
        tableAddedBy: 'Added By',
        tableAddedAt: 'Added At',
        tableNote: 'Note',
        editNote: 'Edit note',
        saveNote: 'Save note',
        lastEdited: 'Last edited {{time}}',
        tableRemove: 'Remove',
        kindEmail: 'Email',
        kindWildcard: 'Wildcard',
        kindRegex: 'Regex',
        kindInvalid: 'Invalid',
        detected: 'Detected as: {{kind}}',
        validationError: 'Must be a valid email, wildcard (*@domain.com), or a valid regular expression',
        removeConfirm: 'Remove {{email}} from allowlist?',
      },
    },

    // ── Common ────────────────────────────────────────────────────────────────
    common: {
      confirm: 'Confirm',
      cancel: 'Cancel',
      save: 'Save',
      saving: 'Saving\u2026',
      delete: 'Delete',
      close: 'Close',
      by: 'by {{name}}',
      me: 'me',
      min: '{{count}} min',
      user: 'User',
      primary: 'primary',
      main: 'main',
      noDetails: 'No additional details.',
      unexpectedError: 'An unexpected error occurred',
      providerGoogle: 'Google',
      providerMicrosoft: 'Microsoft',
      conflictRetry: 'The data was changed by another session — please refresh and try again.',
    },
  },
}

export default en
